import { computed, ref, watch } from 'vue';
import type { Router } from 'vue-router';

export type Theme = 'light' | 'dark';

// Keep in sync with the inline script in index.html, which applies the class
// before the app loads to avoid a flash of light mode.
const STORAGE_KEY = 'theme';
const DARK_CLASS = 'app-dark';

// Participant-facing pages always use light mode.
const LIGHT_ONLY_PATHS = ['/collect', '/feedback'];

export const isLightOnlyPath = (path: string) =>
  LIGHT_ONLY_PATHS.some((p) => path === p || path.startsWith(`${p}/`));

const readStoredTheme = (): Theme => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const theme = ref<Theme>(readStoredTheme());

/**
 * Keeps the dark mode class on <html> in sync with the selected theme
 * and the current route. Call once when the router is created.
 */
export const initTheme = (router: Router) => {
  watch(
    [theme, () => router.currentRoute.value.path],
    ([t, path]) => {
      document.documentElement.classList.toggle(
        DARK_CLASS,
        t === 'dark' && !isLightOnlyPath(path),
      );
    },
    { immediate: true },
  );
};

export const useTheme = () => {
  const isDark = computed(() => theme.value === 'dark');

  const toggleTheme = () => {
    theme.value = isDark.value ? 'light' : 'dark';
    try {
      localStorage.setItem(STORAGE_KEY, theme.value);
    } catch {
      // Storage unavailable (e.g. private mode): the theme lasts for this session only.
    }
  };

  return {
    isDark,
    toggleTheme,
  };
};
